import { ArrowRight, Award } from "lucide-react";
import ConceptHeader from "./ConceptHeader";
import { ConceptFooter } from "./LayoutServicePro";

/**
 * "Premium Authority" layout — serif-heavy, results-forward.
 * Used by law firms and accounting firms.
 * Distinct: centered serif hero, results-numbers band, attorney/partner
 * profile spotlight, practice-area list, accolades strip.
 */
export default function LayoutPremiumAuthority({ c }) {
  const t = c.theme;
  return (
    <div style={{ background: t.bg, color: t.text, fontFamily: t.fontBody, minHeight: "100vh" }}>
      <ConceptHeader industry={c.industry} />

      {/* Faux nav */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-8 pb-2 flex items-center justify-between">
        <p style={{ fontFamily: t.fontDisplay, fontSize: 28 }}>{c.name}</p>
        <nav className="hidden md:flex items-center gap-8 text-[13px] uppercase tracking-[0.18em]" style={{ color: t.muted }}>
          <span>Practice</span><span>Results</span><span>Attorneys</span><span>Insights</span>
          <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-medium tracking-normal"
                style={{ background: t.accent, color: "#fff" }}>
            {c.hero.ctaPrimary}
          </span>
        </nav>
      </div>

      {/* HERO — centered editorial */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-16 text-center">
        <p className="text-[12px] uppercase tracking-[0.28em]" style={{ color: t.accent }}>{c.hero.eyebrow}</p>
        <h1 className="mt-8 leading-[1.02] mx-auto max-w-4xl"
            style={{ fontFamily: t.fontDisplay, fontSize: "clamp(48px, 7vw, 96px)", fontWeight: 500 }}>
          {c.hero.title}
        </h1>
        <p className="mt-8 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto" style={{ color: t.muted }}>
          {c.hero.sub}
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <span className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-medium text-[15px]"
                style={{ background: t.accent, color: "#fff" }}>
            {c.hero.ctaPrimary} <ArrowRight size={16}/>
          </span>
          <span className="inline-flex items-center gap-2 px-7 py-4 rounded-full font-medium text-[15px] border"
                style={{ borderColor: t.muted, color: t.text }}>
            {c.hero.ctaSecondary}
          </span>
        </div>
      </section>

      {/* Hero photo full-bleed */}
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="aspect-[16/7] rounded-2xl overflow-hidden"
             style={{ background: `url(${c.images.hero}) center/cover` }}/>
      </div>

      {/* RESULTS — numbers band */}
      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="text-[12px] uppercase tracking-[0.28em]" style={{ color: t.accent }}>Results that speak</p>
            <h2 className="mt-4 leading-tight" style={{ fontFamily: t.fontDisplay, fontSize: "clamp(32px, 4vw, 52px)", fontWeight: 500 }}>
              Numbers from the last year.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px"
               style={{ background: t.muted + "22", border: `1px solid ${t.muted}22`, borderRadius: 16, overflow: "hidden" }}>
            {(c.results || []).map((r) => (
              <div key={r.v} className="p-10" style={{ background: t.bg }}>
                <p style={{ fontFamily: t.fontDisplay, fontSize: 48, color: t.accent }}>{r.k}</p>
                <p className="mt-2 text-[13px] uppercase tracking-[0.22em]" style={{ color: t.muted }}>{r.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRACTICE AREAS — elegant 2-col list */}
      <section className="py-24 md:py-28" style={{ background: t.surface }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-4">
            <p className="text-[12px] uppercase tracking-[0.28em]" style={{ color: t.accent }}>Practice areas</p>
            <h2 className="mt-4 leading-tight" style={{ fontFamily: t.fontDisplay, fontSize: "clamp(32px, 4vw, 48px)", fontWeight: 500 }}>
              Counsel across the matters that matter.
            </h2>
            <p className="mt-6" style={{ color: t.muted }}>
              Every engagement begins with a confidential consultation. We'll tell you straight whether we're the right fit.
            </p>
          </div>
          <div className="lg:col-span-8 divide-y" style={{ borderColor: t.muted + "22" }}>
            {c.services.map((s, i) => (
              <div key={s.title} className="py-7 grid grid-cols-12 gap-4 items-baseline" style={{ borderTop: i === 0 ? `1px solid ${t.muted}22` : undefined }}>
                <p className="col-span-1 font-mono text-[13px]" style={{ color: t.accent }}>0{i+1}</p>
                <h3 className="col-span-4" style={{ fontFamily: t.fontDisplay, fontSize: 26 }}>{s.title}</h3>
                <p className="col-span-7 text-[15px] leading-relaxed" style={{ color: t.muted }}>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNER SPOTLIGHT */}
      <section className="py-24 md:py-28">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden"
                 style={{ background: `url(${c.images.portrait || c.images.secondary}) center/cover` }}/>
          </div>
          <div className="lg:col-span-7">
            <p className="text-[12px] uppercase tracking-[0.28em]" style={{ color: t.accent }}>From the founder</p>
            <p className="mt-6 leading-tight"
               style={{ fontFamily: t.fontDisplay, fontSize: "clamp(26px, 3.4vw, 42px)", fontWeight: 500 }}>
              "{c.testimonial.quote}"
            </p>
            <p className="mt-6 text-lg" style={{ color: t.muted }}>
              That kind of result isn't an accident — it's the standard we hold ourselves to on every matter, every client, every time.
            </p>
            <p className="mt-8 text-[13px] uppercase tracking-[0.22em]" style={{ color: t.muted }}>— {c.testimonial.author}</p>
          </div>
        </div>
      </section>

      {/* ACCOLADES STRIP */}
      <section className="py-12 border-y" style={{ background: t.surface, borderColor: t.muted + "22" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-wrap items-center justify-center gap-10" style={{ color: t.muted }}>
          {["Super Lawyers", "Best of the Bar", "AV Preeminent", "Top 40 Under 40", "Chambers Listed"].map((a) => (
            <div key={a} className="flex items-center gap-2 text-[13px] uppercase tracking-[0.22em]">
              <Award size={14} style={{ color: t.accent }}/> {a}
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24" style={{ background: t.bg }}>
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2 className="leading-tight"
              style={{ fontFamily: t.fontDisplay, fontSize: "clamp(36px, 4.5vw, 64px)", fontWeight: 500 }}>
            {c.finalCta}.
          </h2>
          <p className="mt-6 text-lg" style={{ color: t.muted }}>Confidential. No-obligation. Same-week scheduling.</p>
          <div className="mt-10 flex justify-center">
            <span className="inline-flex items-center gap-2 px-9 py-4 rounded-full font-medium text-[15px]"
                  style={{ background: t.accent, color: "#fff" }}>
              {c.hero.ctaPrimary} <ArrowRight size={16}/>
            </span>
          </div>
        </div>
      </section>

      <ConceptFooter accent={t.accent} muted={t.muted} bg={t.bg}/>
    </div>
  );
}
